import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ProgressBar from "../components/layout/ProgressBar";
import OutboundCallModal from "../components/modals/OutboundCallModal";
import CommandPalette from "../components/search/CommandPalette";
import NotificationsPanel from "../components/notifications/NotificationsPanel";
import Toast from "../components/common/Toast";
import useToast from "../hooks/useToast";
import useSidebarState from "../hooks/useSidebarState";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { ShellContext } from "./useShellContext";
import { startOutboundCall } from "../services/outboundApi";
import { getCallLiveSummary } from "../services/callsApi";
import { getAiConfigs } from "../services/aiConfigApi";
import { searchWorkspace } from "../services/workspaceSearchApi";
import { getNotifications } from "../services/notificationsApi";
import { normalizeApiError } from "../utils/errors";

const defaultPageControls = {
  onRefresh: null,
  refreshEvery: "Manual",
  lastUpdated: "--",
  isRefreshing: false,
};

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebar = useSidebarState();
  const { toasts, showToast, removeToast } = useToast();
  const [, setPageControls] = useState(defaultPageControls);
  const [outboundBusy, setOutboundBusy] = useState(false);
  const [progressWidth, setProgressWidth] = useState(0);
  const [progressActive, setProgressActive] = useState(false);
  const [liveSummary, setLiveSummary] = useState({ active: 0, counts: {} });
  const [liveStatusError, setLiveStatusError] = useState("");
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState("");
  const [notifications, setNotifications] = useState({
    unread_count: 0,
    items: [],
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchItems, setSearchItems] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [aiConfigs, setAiConfigs] = useState([]);
  const [outboundModal, setOutboundModal] = useState({
    open: false,
    phone: "",
    name: "",
    reason: "",
    summary: "",
    ai_config_id: "",
    voice_provider: "openai",
  });

  const {
    isMobile,
    isCollapsed,
    isMobileOpen,
    sidebarWidth,
    toggle,
    closeMobile,
  } = sidebar;

  const showProgress = useCallback(() => {
    setProgressActive(true);
    setProgressWidth(28);
    window.setTimeout(() => setProgressWidth(72), 120);
  }, []);

  const hideProgress = useCallback(() => {
    setProgressWidth(100);
    window.setTimeout(() => {
      setProgressActive(false);
      setProgressWidth(0);
    }, 280);
  }, []);

  useEffect(() => {
    if (isMobile) {
      closeMobile();
    }
  }, [closeMobile, isMobile, location.pathname]);

  useEffect(() => {
    setNotificationsOpen(false);
  }, [location.pathname]);

  const refreshLiveSummary = useCallback(async () => {
    try {
      const data = await getCallLiveSummary();
      setLiveSummary(data || { active: 0, counts: {} });
      setLiveStatusError("");
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      if (!normalizedError.isAbort) {
        setLiveStatusError(normalizedError.message || "Live status unavailable.");
      }
    }
  }, []);

  const refreshNotifications = useCallback(async () => {
    setNotificationsLoading(true);

    try {
      const data = await getNotifications({ limit: 20 });
      setNotifications(data);
      setNotificationsError("");
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      if (!normalizedError.isAbort) {
        setNotificationsError(normalizedError.message || "Notifications unavailable.");
      }
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshLiveSummary();
    const intervalId = window.setInterval(refreshLiveSummary, 15000);

    return () => window.clearInterval(intervalId);
  }, [refreshLiveSummary]);

  useEffect(() => {
    refreshNotifications();
    const intervalId = window.setInterval(refreshNotifications, 30000);

    return () => window.clearInterval(intervalId);
  }, [refreshNotifications]);

  useEffect(() => {
    let cancelled = false;

    getAiConfigs({ limit: 100 })
      .then((result) => {
        if (cancelled) {
          return;
        }

        const activeConfigs = (result.items || []).filter(
          (config) => String(config.status || "").toUpperCase() === "ACTIVE",
        );
        setAiConfigs(activeConfigs);
      })
      .catch(() => {
        if (!cancelled) {
          setAiConfigs([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const openSearch = useCallback((initialQuery = "") => {
    setSearchQuery(initialQuery);
    setSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchItems([]);
    setSearchError("");
    setSearchLoading(false);
  }, []);

  useEffect(() => {
    const handleKeyboardShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openSearch("");
      }
    };

    window.addEventListener("keydown", handleKeyboardShortcut);

    return () => window.removeEventListener("keydown", handleKeyboardShortcut);
  }, [openSearch]);

  useEffect(() => {
    if (!searchOpen) {
      return undefined;
    }

    const query = searchQuery.trim();

    if (query.length < 2) {
      setSearchItems([]);
      setSearchLoading(false);
      setSearchError("");
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setSearchLoading(true);
      setSearchError("");

      try {
        const result = await searchWorkspace(
          { q: query, limit: 10 },
          { signal: controller.signal },
        );
        setSearchItems(result.items || []);
      } catch (error) {
        const normalizedError = normalizeApiError(error);
        if (!normalizedError.isAbort) {
          setSearchError(normalizedError.message || "Search unavailable.");
        }
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [searchOpen, searchQuery]);

  const registerPageControls = useCallback((nextControls) => {
    setPageControls((current) => ({
      ...current,
      ...nextControls,
    }));
  }, []);

  const resetPageControls = useCallback(() => {
    setPageControls(defaultPageControls);
  }, []);

  const openOutboundModal = useCallback((prefill = {}) => {
    setOutboundModal({
      open: true,
      phone: prefill.phone || "",
      name: prefill.name || "",
      reason: prefill.reason || "",
      summary: prefill.summary || "",
      ai_config_id: prefill.ai_config_id || "",
    });
  }, []);

  const closeOutboundModal = useCallback(() => {
    if (outboundBusy) {
      return;
    }

    setOutboundModal((current) => ({
      ...current,
      open: false,
    }));
  }, [outboundBusy]);

  const runBulkCalls = useCallback(
    async (targets, options = {}) => {
      const confirmMessage =
        options.confirmMessage ||
        `Start calling ${targets.length} patients? Calling AI will call each one sequentially.`;

      if (!targets.length) {
        showToast("No valid targets with phone numbers selected.", true);
        return { success: 0, failed: 0, cancelled: false };
      }

      if (!window.confirm(confirmMessage)) {
        return { success: 0, failed: 0, cancelled: true };
      }

      showToast(
        `Starting bulk calls for ${targets.length} patients...`,
        false,
        5000,
      );

      let success = 0;
      let failed = 0;

      for (const target of targets) {
        try {
          const data = await startOutboundCall({
            phone: target.phone,
            name: target.name,
            reason: target.reason || "follow-up",
          });
          if (data?.status === "failed" || data?.ari?.accepted === false) {
            throw new Error(data?.ari?.message || "ARI did not accept the call");
          }
          success += 1;
        } catch (error) {
          failed += 1;
        }

        await new Promise((resolve) => {
          window.setTimeout(resolve, options.delayMs || 2000);
        });
      }

      showToast(
        `Bulk call complete: ${success} started, ${failed} failed.`,
        failed > 0,
        6000,
      );

      return { success, failed, cancelled: false };
    },
    [showToast],
  );

  const outletContext = useMemo(
    () => ({
      showToast,
      registerPageControls,
      resetPageControls,
      openOutboundModal,
      runBulkCalls,
    }),
    [
      showToast,
      registerPageControls,
      resetPageControls,
      openOutboundModal,
      runBulkCalls,
    ],
  );

  return (
    <>
      <ProgressBar width={progressWidth} active={progressActive} />
      <div className="shell">
        {/* <UtilityBar /> */}
        <div
          className="app-shell-grid"
          style={{
            gridTemplateColumns: `${sidebarWidth}px minmax(0, 1fr)`,
          }}
        >
          <Sidebar
            collapsed={!isMobile && isCollapsed}
            mobile={isMobile}
            mobileOpen={isMobileOpen}
            width={sidebarWidth}
          />
          <div className="app-content-column">
            <Header
              onToggleSidebar={toggle}
              onOpenOutboundModal={() => openOutboundModal()}
              onOpenSearch={openSearch}
              liveSummary={liveSummary}
              liveStatusError={liveStatusError}
              notificationsOpen={notificationsOpen}
              notificationCount={notifications.unread_count}
              onToggleNotifications={() =>
                setNotificationsOpen((current) => !current)
              }
              notificationsPanel={
                <NotificationsPanel
                  open={notificationsOpen}
                  items={notifications.items}
                  loading={notificationsLoading}
                  error={notificationsError}
                  onRefresh={refreshNotifications}
                  onSelect={(item) => {
                    setNotificationsOpen(false);
                    if (item?.url) {
                      navigate(item.url);
                    }
                  }}
                />
              }
            />
            <main className="app-route-content">
              <ShellContext.Provider value={outletContext}>
                <Outlet context={outletContext} />
              </ShellContext.Provider>
            </main>
            {/* <Footer
              lastUpdated={pageControls.lastUpdated}
              refreshEvery={pageControls.refreshEvery}
              spinning={pageControls.isRefreshing}
              onRefresh={async () => {
                if (!pageControls.onRefresh) {
                  return;
                }

                showProgress();

                try {
                  await pageControls.onRefresh();
                } catch (error) {
                  const normalizedError = normalizeApiError(error);
                  if (!normalizedError.isAbort) {
                    showToast(normalizedError.message, true);
                  }
                } finally {
                  hideProgress();
                }
              }}
            /> */}
          </div>
        </div>
      </div>

      <OutboundCallModal
        open={outboundModal.open}
        form={outboundModal}
        busy={outboundBusy}
        aiConfigs={aiConfigs}
        onClose={closeOutboundModal}
        onChange={(field, value) =>
          setOutboundModal((current) => ({
            ...current,
            [field]: value,
          }))
        }
        onSubmit={async (event) => {
          event.preventDefault();

          const voiceProvider = outboundModal.voice_provider || "openai";
          const isElevenDemo = voiceProvider === "elevenlabs";

          // Name is optional for the ElevenLabs voice demo (phone is enough).
          if (!outboundModal.phone.trim() || (!isElevenDemo && !outboundModal.name.trim())) {
            showToast(isElevenDemo ? "Phone number required." : "Phone and name required.", true);
            return;
          }

          setOutboundBusy(true);
          showProgress();

          try {
            const data = await startOutboundCall({
              phone: outboundModal.phone.trim(),
              name: outboundModal.name.trim(),
              reason: outboundModal.reason.trim() || "appointment reminder",
              ai_config_id: outboundModal.ai_config_id || undefined,
              voice_provider: voiceProvider,
            });

            setOutboundModal((current) => ({
              ...current,
              open: false,
            }));
            refreshLiveSummary();
            refreshNotifications();
            const failed = data?.status === "failed" || data?.ari?.accepted === false;
            showToast(
              failed
                ? data?.ari?.message || "Call request was recorded, but ARI did not accept it."
                : `Call start accepted${data?.provider ? ` via ${data.provider}` : ""}${data?.call_id ? ` (${data.call_id})` : ""}`,
              failed,
              failed ? 7000 : 5000,
            );
          } catch (error) {
            const normalizedError = normalizeApiError(error);
            if (!normalizedError.isAbort) {
              showToast(normalizedError.message, true, 7000);
            }
          } finally {
            setOutboundBusy(false);
            hideProgress();
          }
        }}
      />

      <CommandPalette
        open={searchOpen}
        query={searchQuery}
        items={searchItems}
        loading={searchLoading}
        error={searchError}
        onQueryChange={setSearchQuery}
        onClose={closeSearch}
        onSelect={(item) => {
          closeSearch();
          if (item?.url) {
            navigate(item.url);
          }
        }}
      />

      <Toast toasts={toasts} onDismiss={removeToast} />
    </>
  );
}
